// /client/src/components/TwilioMessageQueue.js
// Twilio Message Queue - displays messages directly from Twilio API with all statuses

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import Navigation from './Navigation';

const TwilioMessageQueue = () => {
  // State management
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Get initial 7-day window (today and 6 days back)
  const getDateRange = (offsetWeeks = 0) => {
    const today = new Date();
    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() - (offsetWeeks * 7));
    
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - 6);
    
    return {
      dateFrom: startDate.toISOString().split('T')[0],
      dateTo: endDate.toISOString().split('T')[0]
    };
  };

  // Week offset (0 = current week, 1 = previous week, etc.)
  const [weekOffset, setWeekOffset] = useState(0);

  // Filter state with 7-day default
  const [filters, setFilters] = useState({
    direction: '',
    status: '',
    ...getDateRange(0)
  });

  // State for sorting
  const [sortConfig, setSortConfig] = useState({
    key: 'dateSent',
    direction: 'descending'
  });

  // Twilio message statuses
  const statusOptions = [
    'accepted',
    'queued', 
    'sending',
    'sent',
    'delivered',
    'undelivered',
    'failed',
    'receiving',
    'received',
    'read',
    'canceled',
    'scheduled'
  ];

  const directionOptions = ['inbound', 'outbound-api', 'outbound-call', 'outbound-reply'];

  // Status descriptions for tooltips
  const statusDescriptions = {
    accepted: 'Twilio has received your API request to send a message',
    queued: 'Message is queued to be sent out',
    sending: 'Twilio is in the process of dispatching the message',
    sent: 'Message has been sent to the carrier',
    delivered: 'Carrier has confirmed delivery to the recipient',
    undelivered: 'Carrier could not deliver the message',
    failed: 'Message could not be sent',
    receiving: 'Twilio is receiving an inbound message',
    received: 'Inbound message has been received',
    read: 'Message has been read (WhatsApp only)',
    canceled: 'Message was canceled before being sent',
    scheduled: 'Message is scheduled for future delivery'
  };

  // Twilio error code descriptions
  const errorCodeDescriptions = {
    10001: 'Account is not active',
    11100: 'Invalid URL format',
    11200: 'HTTP retrieval failure',
    11205: 'HTTP connection failure',
    11210: 'HTTP bad host name',
    11215: 'HTTP too many redirects',
    11220: 'Invalid HTTP response',
    12100: 'Document parse failure',
    12200: 'Schema validation warning',
    12300: 'Invalid Content-Type',
    12400: 'Internal failure',
    13221: 'Dial: Invalid phone number format',
    13223: 'Dial: Cannot reach phone number',
    13224: 'Dial: Invalid timeout',
    13225: 'Dial: Forbidden phone number',
    13226: 'Dial: Invalid CallerId',
    14101: 'Invalid "From" phone number',
    14102: 'Invalid "To" phone number', 
    14103: 'Invalid "Body" content',
    14107: 'Message body too large',
    14108: 'Invalid "From" and "To" pair',
    20001: 'Unknown parameters',
    20003: 'Permission denied',
    20004: 'Method not allowed',
    20005: 'Account requires more info',
    20006: 'Access denied',
    20007: 'Page not found',
    20008: 'Request timeout',
    20009: 'Internal service error',
    20010: 'Account is suspended',
    20011: 'Account is closed',
    20404: 'Resource not found',
    20426: 'Upgrade required',
    20429: 'Too many requests',
    20500: 'Internal server error',
    21201: 'No "To" number specified',
    21202: 'No "From" number specified',
    21203: 'Invalid "From" number',
    21207: '"From" number not verified',
    21208: '"From" number not SMS capable',
    21210: '"From" number not valid for GEO',
    21211: 'Invalid "To" number',
    21212: 'Invalid "Body"',
    21214: '"To" number cannot receive SMS',
    21215: 'Account not authorized for GEO',
    21216: 'Account not authorized',
    21217: 'Phone number requires address',
    21219: 'Phone number not verified',
    21220: 'Invalid length for phone number',
    21265: 'Message body word length limit exceeded',
    21401: 'Invalid phone number',
    21402: 'Invalid URL',
    21403: 'Invalid method',
    21404: 'Invalid parameter value',
    21405: 'Account lacks capability',
    21407: 'Cannot send messages to shortcodes',
    21408: 'Permission to send denied',
    21421: 'Invalid PhoneSid',
    21450: 'Reply rate limit reached',
    21451: 'A]2P 10DLC - Campaign Registry',
    21452: 'A2P 10DLC - Phone Number Required',
    21453: 'A2P 10DLC - Campaign not created',
    21454: 'A2P 10DLC - Campaign pending',
    21455: 'A2P 10DLC - Campaign invalid status',
    21456: 'A2P 10DLC - Brand not found',
    21457: 'A2P 10DLC - Brand invalid status',
    21458: 'A2P 10DLC - Message Service not found',
    21459: 'A2P 10DLC - Campaign suspended',
    21460: 'A2P 10DLC - Campaign expired',
    21461: 'A2P 10DLC - Campaign messaging limit',
    21462: 'A2P 10DLC - Registration required',
    21463: 'A2P 10DLC - Number not in campaign',
    21464: 'A2P 10DLC - Brand suspended',
    21467: 'A2P 10DLC - Brand unverified',
    21468: 'A2P 10DLC - Brand registration failed',
    21469: 'A2P 10DLC - Campaign use case invalid',
    21501: 'Invalid Sender ID',
    21502: 'Sender ID not authorized',
    21503: 'Invalid messaging service',
    21504: 'Messaging service inactive',
    21506: 'Messaging service has no senders',
    21601: 'Phone number not in messaging service',
    21602: 'Message body too long',
    21603: 'Max price too low',
    21604: 'Invalid "Body" and "MediaUrl"',
    21605: 'Invalid validity period',
    21606: '"From" required for outbound',
    21608: 'Cannot send to unsubscribed',
    21610: 'Recipient opted out',
    21611: 'Messaging service cannot be deleted',
    21612: 'Cannot add shortcode',
    21614: '"To" is not mobile number',
    21615: 'Invalid messenger channel address',
    21617: 'Message body empty',
    21618: 'MMS too large',
    21619: 'Media download timed out',
    21620: 'Invalid media URL',
    21621: 'Media not supported',
    21623: 'Invalid message pricing',
    21626: 'Invalid encoding',
    21629: 'Max message body exceeded',
    21630: 'Address blocked',
    21632: 'Trial cannot send to unverified',
    21635: 'Trial account limit',
    21637: 'Messaging service disabled',
    21638: 'Toll-free verification pending',
    21639: 'Toll-free verification failed',
    21640: 'Message delivery blocked',
    21641: 'Number lacks MMS capability',
    21645: 'Number verification required',
    21656: 'Toll-free sender limited',
    21657: 'Toll-free verification rejected',
    21658: 'Toll-free requires retry',
    21659: 'Message blocked by carrier',
    21660: 'Toll-free verification in review',
    30001: 'Queue overflow',
    30002: 'Account suspended',
    30003: 'Unreachable destination',
    30004: 'Message blocked',
    30005: 'Unknown destination',
    30006: 'Landline or unreachable carrier',
    30007: 'Carrier violation',
    30008: 'Unknown error',
    30009: 'Missing segment',
    30010: 'Message price exceeds max',
    30017: 'Carrier requires delivery confirmation',
    30018: 'Carrier does not support message',
    30019: 'Delivery confirmation timeout',
    30020: 'Cannot reach destination carrier',
    30021: 'Channel became inactive',
    30022: 'Regional filtering applied',
    30023: 'Carrier rejected message',
    30024: 'Missing or invalid parameters',
    30025: 'Regulatory compliance failure',
    30026: 'Geographically restricted',
    30027: 'Unreachable or unallocated',
    30028: 'Messaging restricted by carrier',
    30029: 'Spam or fraud filter',
    30030: 'MMS not supported by carrier',
    30031: 'MMS media not available',
    30032: 'Rate limit exceeded',
    30033: 'Message too long for carrier',
    30034: 'Service unavailable',
    30035: 'Sending capacity exceeded',
    30036: 'Country or region not allowed',
    30037: 'Trial blocked destination',
    30038: 'Trial max recipients reached',
    30039: 'Toll-free sent restricted content',
    30040: 'Carrier network congestion',
    30041: 'Content not delivered',
    30042: 'Recipient mobile error',
    30043: 'Carrier rejected submission',
    30044: 'Carrier rejected message type',
    30045: 'Service temporarily unavailable',
    30052: 'Carrier limits exceeded',
    30053: 'Carrier blocked opt-out recipient',
    30054: 'Daily rate limit exceeded',
    30055: 'Content violates regulations',
    30056: 'Unknown carrier error',
    30057: 'Long code not enabled',
    30058: 'Messaging capacity depleted',
    30059: 'Carrier restriction',
    30060: 'Carrier temporary failure',
    30061: 'Long code not verified',
    30062: 'Content restricted',
    30064: 'Invalid destination number',
    30065: 'Carrier requires opt-in',
    32001: 'Invalid parameters',
    32002: 'Missing required parameter',
    32003: 'Invalid phone number format',
    32004: 'Conversation already exists',
    32006: 'Invalid token',
    63001: 'Channel could not deliver',
    63002: 'Channel unknown error',
    63003: 'Channel authentication failed',
    63005: 'Channel outside delivery window',
    63007: 'Recipient undeliverable',
    63008: 'Channel unavailable',
    63009: 'Channel request failed',
    63010: 'Channel session expired',
    63011: 'Channel rate limited',
    63012: 'Channel content rejected',
    63013: 'Channel attachment failed',
    63014: 'Channel unreachable',
    63015: 'Channel recipient opted out',
    63016: 'Channel sender blocked',
    63017: 'Channel insufficient balance',
    63018: 'Channel user outside session',
    63019: 'Channel template not found',
    63020: 'Channel template unpproved',
    63021: 'Channel media unavailable'
  };

  const getErrorDescription = (errorCode) => {
    if (!errorCode) return '';
    const code = parseInt(errorCode);
    return errorCodeDescriptions[code] || `Unknown error code: ${errorCode}`;
  };

  // Fetch messages from Twilio via backend API
  const fetchMessages = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('No authentication token found');
        setLoading(false);
        return;
      }

      const params = new URLSearchParams({
        limit: 500 // Get all messages in date range
      });

      // Add active filters
      if (filters.direction) params.append('direction', filters.direction);
      if (filters.status) params.append('status', filters.status);
      if (filters.dateFrom) params.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) params.append('dateTo', filters.dateTo);

      const response = await fetch(`/api/twilio/messages?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setMessages(data.messages || []);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch Twilio messages');
      }
    } catch (err) {
      setError('Failed to load Twilio messages');
      console.error('Error fetching Twilio messages:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Navigate weeks
  const goToPreviousWeek = () => {
    const newOffset = weekOffset + 1;
    setWeekOffset(newOffset);
    const newDates = getDateRange(newOffset);
    setFilters(prev => ({ ...prev, ...newDates }));
  };

  const goToNextWeek = () => {
    if (weekOffset > 0) {
      const newOffset = weekOffset - 1;
      setWeekOffset(newOffset);
      const newDates = getDateRange(newOffset);
      setFilters(prev => ({ ...prev, ...newDates }));
    }
  };

  const goToCurrentWeek = () => {
    setWeekOffset(0);
    const newDates = getDateRange(0);
    setFilters(prev => ({ ...prev, ...newDates }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setWeekOffset(0);
    setFilters({
      direction: '',
      status: '',
      ...getDateRange(0)
    });
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedMessages = useMemo(() => {
    let sorted = [...messages];
    
    if (sortConfig !== null) {
      sorted.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        if (sortConfig.key === 'dateSent' || sortConfig.key === 'dateCreated' || sortConfig.key === 'dateUpdated') {
          aValue = new Date(aValue || 0);
          bValue = new Date(bValue || 0);
        } else if (sortConfig.key === 'price') {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        } else {
          aValue = (aValue || '').toString().toLowerCase();
          bValue = (bValue || '').toString().toLowerCase();
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    
    return sorted;
  }, [messages, sortConfig]);

  // Count messages by status
  const statusCounts = useMemo(() => {
    const counts = {};
    messages.forEach(msg => {
      counts[msg.status] = (counts[msg.status] || 0) + 1;
    });
    return counts;
  }, [messages]);

  // Check for problem statuses
  const hasProblems = statusCounts.failed > 0 || statusCounts.undelivered > 0;

  const getSortIcon = (columnName) => {
    const iconStyle = {
      width: '12px',
      height: '12px',
      marginLeft: '8px',
      display: 'inline-block'
    };

    if (sortConfig.key !== columnName) {
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 5.83L15.17 9H13v6h-2V9H8.83L12 5.83zM12 18.17L8.83 15H11V9h2v6h2.17L12 18.17z"/>
        </svg>
      );
    }
    
    if (sortConfig.direction === 'ascending') {
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 6.83L15.17 10H13v8h-2v-8H8.83L12 6.83z"/>
        </svg>
      );
    } else {
      return (
        <svg style={iconStyle} viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 17.17L8.83 14H11V6h2v8h2.17L12 17.17z"/>
        </svg>
      );
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const formatDateRange = () => {
    const from = new Date(filters.dateFrom);
    const to = new Date(filters.dateTo);
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    return `${from.toLocaleDateString('en-US', options)} – ${to.toLocaleDateString('en-US', options)}`;
  };

  const getStatusColor = (status) => {
    const statusColors = {
      accepted: 'is-info is-light',
      queued: 'is-warning',
      sending: 'is-info',
      sent: 'is-primary',
      delivered: 'is-success',
      undelivered: 'is-danger is-light',
      failed: 'is-danger',
      receiving: 'is-info is-light',
      received: 'is-primary',
      read: 'is-success is-light',
      canceled: 'is-dark',
      scheduled: 'is-link'
    };
    return statusColors[status] || 'is-light';
  };

  const getDirectionLabel = (direction) => {
    const labels = {
      'inbound': '↓ Received',
      'outbound-api': '↑ Sent Message',
      'outbound-call': '↑ Outbound Call',
      'outbound-reply': '↑ Outbound Reply'
    };
    return labels[direction] || direction || 'Unknown';
  };

  const getDirectionColor = (direction) => {
    if (direction === 'inbound') return 'is-info';
    return 'is-primary';
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return 'N/A';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  const truncateBody = (body, maxLength = 50) => {
    if (!body) return '[No content]';
    if (body.length <= maxLength) return body;
    return body.substring(0, maxLength) + '...';
  };

  const formatPrice = (price, priceUnit) => {
    if (!price) return '—';
    const amount = Math.abs(parseFloat(price));
    return `-$${amount.toFixed(4)} ${priceUnit || 'USD'}`;
  };

  const hasActiveFilters = filters.direction || filters.status;

  if (loading && messages.length === 0) {
    return (
      <>
        <section className="theme-light m-0 p-0" data-theme="light">
          <div className='is-flex is-flex-direction-column is-justify-content-center'>
            <Navigation />
            <div className='mt-6 pt-6 container'>
              <div className="has-text-centered py-6">
                <div className="is-loading"></div>
                <p>Loading Twilio message queue...</p>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <section className="theme-light m-0 p-0" data-theme="light">
        <div className='is-flex is-flex-direction-column is-justify-content-center'>
          
          <Navigation />

          <div className='mt-2 pt-6 container px-6'>
            <div className='pb-4 is-flex is-justify-content-space-between is-align-items-center'>
              <div className="is-flex is-align-items-center">
                <h1 className='h1 my-2'>Twilio Message Queue</h1>
                {hasProblems && (
                  <span 
                    className="tag is-danger is-medium ml-3"
                    style={{ animation: 'pulse 2s infinite' }}
                  >
                    ⚠️ {(statusCounts.failed || 0) + (statusCounts.undelivered || 0)} Problem{((statusCounts.failed || 0) + (statusCounts.undelivered || 0)) !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <button 
                className="button is-light"
                onClick={fetchMessages}
                disabled={loading}
              >
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>

            {/* Date Range Display */}
            <div className="mb-4 has-text-centered">
              <p className="is-size-5 has-text-weight-semibold">
                {formatDateRange()}
              </p>
              <p className="is-size-7 has-text-grey">
                {messages.length} message{messages.length !== 1 ? 's' : ''} in this period
              </p>
            </div>

            {/* Status Summary Cards */}
            {Object.keys(statusCounts).length > 0 && (
              <div className="columns is-multiline mb-4">
                {Object.entries(statusCounts).map(([status, count]) => (
                  <div className="column is-2" key={status}>
                    <div 
                      className={`box has-text-centered p-3 ${filters.status === status ? 'has-background-light' : ''}`}
                      style={{ 
                        cursor: 'pointer',
                        border: filters.status === status ? '2px solid #3273dc' : '1px solid #dbdbdb'
                      }}
                      onClick={() => setFilters(prev => ({ 
                        ...prev, 
                        status: prev.status === status ? '' : status 
                      }))}
                    >
                      <p className="is-size-7 has-text-grey mb-1" style={{ textTransform: 'capitalize' }}>
                        {status}
                      </p>
                      <p className={`is-size-4 has-text-weight-bold ${
                        status === 'failed' || status === 'undelivered' ? 'has-text-danger' :
                        status === 'delivered' ? 'has-text-success' :
                        status === 'queued' || status === 'sending' ? 'has-text-warning-dark' :
                        'has-text-dark'
                      }`}>
                        {count}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Problem Alert Banner */}
            {hasProblems && !filters.status && (
              <div 
                className="notification is-danger is-light mb-4"
                style={{ 
                  borderLeft: '4px solid #f14668',
                  animation: 'fadeIn 0.3s ease'
                }}
              >
                <div className="is-flex is-justify-content-space-between is-align-items-center">
                  <div>
                    <strong>⚠️ Delivery Issues:</strong> 
                    {statusCounts.failed > 0 && ` ${statusCounts.failed} failed`}
                    {statusCounts.failed > 0 && statusCounts.undelivered > 0 && ','}
                    {statusCounts.undelivered > 0 && ` ${statusCounts.undelivered} undelivered`}
                  </div>
                  <div>
                    {statusCounts.failed > 0 && (
                      <button 
                        className="button is-danger is-small mr-2"
                        onClick={() => setFilters(prev => ({ ...prev, status: 'failed' }))}
                      >
                        View Failed
                      </button>
                    )}
                    {statusCounts.undelivered > 0 && (
                      <button 
                        className="button is-warning is-small"
                        onClick={() => setFilters(prev => ({ ...prev, status: 'undelivered' }))}
                      >
                        View Undelivered
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="notification is-danger">
                <button 
                  className="delete" 
                  onClick={() => setError('')}
                ></button>
                {error}
                <br />
                <button 
                  className="button is-small is-light mt-2" 
                  onClick={fetchMessages}
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Filter Section */}
            <div className="box mb-4">
              <div className="columns">
                <div className="column is-2">
                  <div className="field">
                    <label className="pt-0 label">Direction</label>
                    <div className="control">
                      <div className="select is-fullwidth">
                        <select
                          name="direction"
                          value={filters.direction}
                          onChange={handleFilterChange}
                        >
                          <option value="">All Directions</option>
                          {directionOptions.map(dir => (
                            <option key={dir} value={dir}>
                              {getDirectionLabel(dir)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="column is-2">
                  <div className="field">
                    <label className="pt-0 label">Status</label>
                    <div className="control">
                      <div className={`select is-fullwidth ${filters.status === 'failed' || filters.status === 'undelivered' ? 'is-danger' : ''}`}>
                        <select
                          name="status"
                          value={filters.status}
                          onChange={handleFilterChange}
                        >
                          <option value="">All Statuses</option>
                          {statusOptions.map(status => (
                            <option key={status} value={status}>
                              {status.charAt(0).toUpperCase() + status.slice(1)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="column is-3">
                  <div className="field">
                    <label className="pt-0 label">From Date</label>
                    <div className="control">
                      <input
                        type="date"
                        className="input"
                        name="dateFrom"
                        value={filters.dateFrom}
                        onChange={handleFilterChange}
                      />
                    </div>
                  </div>
                </div>

                <div className="column is-3">
                  <div className="field">
                    <label className="pt-0 label">To Date</label>
                    <div className="control">
                      <input
                        type="date"
                        className="input"
                        name="dateTo"
                        value={filters.dateTo}
                        onChange={handleFilterChange}
                      />
                    </div>
                  </div>
                </div>

                <div className="column is-2">
                  <div className="field">
                    <label className="pt-0 label">&nbsp;</label>
                    <div className="control">
                      <button 
                        className="button is-light is-fullwidth"
                        onClick={clearFilters}
                        disabled={!hasActiveFilters && weekOffset === 0}
                      >
                        Reset Filters
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Active Filter Tags */}
            {hasActiveFilters && (
              <div className="mb-4">
                <div className="tags">
                  {filters.direction && (
                    <span className="tag is-info">
                      Direction: {getDirectionLabel(filters.direction)}
                      <button 
                        className="delete is-small" 
                        onClick={() => setFilters(prev => ({ ...prev, direction: '' }))}
                      ></button>
                    </span>
                  )}
                  {filters.status && (
                    <span className={`tag ${filters.status === 'failed' || filters.status === 'undelivered' ? 'is-danger' : 'is-info'}`}>
                      Status: {filters.status.charAt(0).toUpperCase() + filters.status.slice(1)}
                      <button 
                        className="delete is-small" 
                        onClick={() => setFilters(prev => ({ ...prev, status: '' }))}
                      ></button>
                    </span>
                  )}
                </div>
              </div>
            )}

            {sortedMessages.length === 0 && !loading && !error ? (
              <div className="notification is-info is-light">
                <p>
                  {hasActiveFilters
                    ? 'No messages match your filter criteria.'
                    : 'No messages found in this date range.'}
                </p>
              </div>
            ) : (
              <>
                <div className="table-container">
                  <table className="table is-fullwidth is-hoverable is-striped">
                    <thead>
                      <tr>
                        <th 
                          onClick={() => handleSort('dateSent')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'dateSent' ? 'colorBlack' : 'colorBlue'}
                        >
                          Date Sent {getSortIcon('dateSent')}
                        </th>
                        <th 
                          onClick={() => handleSort('direction')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'direction' ? 'colorBlack' : 'colorBlue'}
                        >
                          Direction {getSortIcon('direction')}
                        </th>
                        <th 
                          onClick={() => handleSort('from')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'from' ? 'colorBlack' : 'colorBlue'}
                        >
                          From {getSortIcon('from')}
                        </th>
                        <th 
                          onClick={() => handleSort('to')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'to' ? 'colorBlack' : 'colorBlue'}
                        >
                          To {getSortIcon('to')}
                        </th>
                        <th style={{ maxWidth: '200px' }}>Message</th>
                        <th 
                          onClick={() => handleSort('status')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'status' ? 'colorBlack' : 'colorBlue'}
                        >
                          Status {getSortIcon('status')}
                        </th>
                        <th 
                          onClick={() => handleSort('price')}
                          style={{ cursor: 'pointer', userSelect: 'none' }}
                          className={sortConfig.key === 'price' ? 'colorBlack' : 'colorBlue'}
                        >
                          Cost {getSortIcon('price')}
                        </th>
                        <th>SID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedMessages.map((message) => {
                        const isProblem = message.status === 'failed' || message.status === 'undelivered';
                        
                        return (
                          <tr 
                            key={message.sid}
                            style={{ 
                              backgroundColor: isProblem ? '#fff5f5' : 'transparent',
                              borderLeft: isProblem ? '4px solid #f14668' : 'none'
                            }}
                          >
                            <td className="is-size-7" style={{ paddingLeft: isProblem ? '12px' : undefined }}>
                              {isProblem && (
                                <span 
                                  style={{ 
                                    display: 'inline-block',
                                    width: '8px',
                                    height: '8px',
                                    borderRadius: '50%',
                                    backgroundColor: '#f14668',
                                    marginRight: '6px'
                                  }}
                                  title={statusDescriptions[message.status]}
                                />
                              )}
                              {formatDateTime(message.dateSent || message.dateCreated)}
                            </td>
                            <td>
                              <span className={`tag is-small ${getDirectionColor(message.direction)}`}>
                                {getDirectionLabel(message.direction)}
                              </span>
                            </td>
                            <td className="is-size-7">
                              <code>{formatPhoneNumber(message.from)}</code>
                            </td>
                            <td className="is-size-7">
                              <code>{formatPhoneNumber(message.to)}</code>
                            </td>
                            <td className="is-size-7" style={{ maxWidth: '200px' }}>
                              <span title={message.body}>
                                {truncateBody(message.body)}
                              </span>
                              {message.numMedia > 0 && (
                                <span className="tag is-small is-light ml-2">
                                  📎 {message.numMedia}
                                </span>
                              )}
                            </td>
                            <td>
                              <span 
                                className={`tag is-small ${getStatusColor(message.status)}`}
                                title={statusDescriptions[message.status]}
                              >
                                {message.status ? message.status.charAt(0).toUpperCase() + message.status.slice(1) : 'Unknown'}
                              </span>
                              {message.errorCode && (
                                <span 
                                  className="tag is-small is-danger is-light ml-1"
                                  title={`Error ${message.errorCode}: ${getErrorDescription(message.errorCode)}`}
                                  style={{ cursor: 'help' }}
                                >
                                  {message.errorCode}
                                </span>
                              )}
                            </td>
                            <td className="is-size-7">
                              {formatPrice(message.price, message.priceUnit)}
                            </td>
                            <td className="is-size-7">
                              <code 
                                title={message.sid}
                                style={{ 
                                  fontSize: '0.7rem',
                                  cursor: 'pointer'
                                }}
                                onClick={() => navigator.clipboard.writeText(message.sid)}
                              >
                                {message.sid?.substring(0, 10)}...
                              </code>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Week Navigation */}
                <div className="is-flex is-justify-content-center is-align-items-center mt-5 mb-5">
                  <button
                    className="button is-medium mr-3"
                    onClick={goToPreviousWeek}
                    disabled={loading}
                  >
                    ← Back 7 Days
                  </button>
                  
                  {weekOffset > 0 && (
                    <button
                      className="button is-light is-medium mr-3"
                      onClick={goToCurrentWeek}
                      disabled={loading}
                    >
                      Current Week
                    </button>
                  )}
                  
                  <button
                    className="button is-medium"
                    onClick={goToNextWeek}
                    disabled={loading || weekOffset === 0}
                  >
                    Next 7 Days →
                  </button>
                </div>
              </>
            )}

          </div>

        </div>
      </section>

      {/* CSS for animations */}
      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default TwilioMessageQueue;